import { Component, OnInit, Output, EventEmitter, Input, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Observable, Subject, startWith, map, debounceTime, distinctUntilChanged, switchMap, of, combineLatest } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { BaseDto } from '@models/base/baseDto.model';
import { JuzgadoDto } from '@models/juzgado/juzgadoDto.model';
import { JuzgadoFilter } from '@models/juzgado/juzgadoFilter.model';
import { FueroService } from '@services/fuero/fuero.service';
import { JuridiccionService } from '@services/juridiccion/juridiccion.service';
import { JuzgadoService } from '@services/juzgado/juzgado.service';
import { ProvinciaService } from '@services/provincia/provincia.service';
import { ProvinciaDto } from '@models/provincia/provinciaDto.model';
import { CommonModule } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  standalone:true,
   imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatCardModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatButtonModule
  ],
  selector: 'app-selec-juzgado',
  templateUrl: 'select-juzgado.component.html',
  styleUrls: ['./select-juzgado.component.css']
})
export class SelectJuzgadoComponent implements OnInit, OnDestroy {

  @Output() juzgadoSeleccionado = new EventEmitter<{
    provincia: BaseDto | null,
    jurisdiccion: BaseDto | null,
    fuero: BaseDto | null,
    juzgado: JuzgadoDto | null
  }>();

  @Input() provinciaInicial: ProvinciaDto | null = null;
  @Input() jurisdiccionInicial: BaseDto | null = null;
  @Input() fueroInicial: BaseDto | null = null;
  @Input() juzgadoInicial: JuzgadoDto | null = null;

  private destroy$ = new Subject<void>();

  // Form Controls
  provinciaForm = new FormGroup({
    provincia: new FormControl<ProvinciaDto | null>(null, [Validators.required])
  });

  jurisdiccionForm = new FormGroup({
    jurisdiccion: new FormControl<BaseDto | null>({ value: null, disabled: true }, [Validators.required])
  });

  fueroForm = new FormGroup({
    fuero: new FormControl<BaseDto | null>({ value: null, disabled: true }, [Validators.required])
  });

  juzgadoForm = new FormGroup({
    juzgado: new FormControl<JuzgadoDto | null>({ value: null, disabled: true }, [Validators.required])
  });

  // Autocomplete Controls
  provinciaControl = new FormControl('');
  jurisdiccionControl = new FormControl({ value: '', disabled: true });
  fueroControl = new FormControl({ value: '', disabled: true });
  juzgadoControl = new FormControl({ value: '', disabled: true });

  // Data Arrays
  provincias: ProvinciaDto[] = [];
  jurisdicciones: BaseDto[] = [];
  fueros: BaseDto[] = [];
  juzgados: JuzgadoDto[] = [];

  // Filtered Observables
  provinciasFiltradas$: Observable<ProvinciaDto[]>;
  jurisdiccionesFiltradas$: Observable<BaseDto[]>;
  fuerosFiltrados$: Observable<BaseDto[]>;
  juzgadosFiltrados$: Observable<JuzgadoDto[]>;

  // Loading States
  loadingProvincias = false;
  loadingJurisdicciones = false;
  loadingFueros = false;
  loadingJuzgados = false;

  constructor(
    private provinciaService: ProvinciaService,
    private fueroService: FueroService,
    private juzgadoService: JuzgadoService,
    private jurisdiccionService: JuridiccionService
  ) {
    this.provinciasFiltradas$ = this.provinciaControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filtrarProvincias(value || ''))
    );

    this.jurisdiccionesFiltradas$ = this.jurisdiccionControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filtrarJurisdicciones(value || ''))
    );

    this.fuerosFiltrados$ = this.fueroControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filtrarFueros(value || ''))
    );

    this.juzgadosFiltrados$ = this.juzgadoControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filtrarJuzgados(value || ''))
    );
  }

  ngOnInit(): void {
    this.cargarDatosIniciales();
    this.configurarCambiosProvincia();
    this.configurarCambiosJurisdiccion();
    this.configurarCambiosFuero();
    this.configurarCambiosJuzgado();
    this.establecerValoresIniciales();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private cargarDatosIniciales(): void {
    this.cargarProvincias();
    this.cargarFueros();
  }

  private cargarProvincias(): void {
    this.loadingProvincias = true;
    this.provinciaService.getAll()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loadingProvincias = false)
      )
      .subscribe({
        next: (provincias) => {
          this.provincias = provincias;
        },
        error: (error) => {
          console.error('Error al cargar provincias:', error);
        }
      });
  }

  private cargarFueros(): void {
    this.loadingFueros = true;
    this.fueroService.getAll()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loadingFueros = false)
      )
      .subscribe({
        next: (fueros) => {
          this.fueros = fueros;
        },
        error: (error) => {
          console.error('Error al cargar fueros:', error);
        }
      });
  }

  private configurarCambiosProvincia(): void {
    this.provinciaForm.get('provincia')?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged()
      )
      .subscribe((provincia: ProvinciaDto | null) => {
        if (provincia) {
          this.habilitarJurisdiccion();
          this.filtrarJurisdiccionesPorProvincia();
        } else {
          this.deshabilitarJurisdiccion();
          this.deshabilitarFuero();
          this.deshabilitarJuzgado();
        }
      });
  }

  private configurarCambiosJurisdiccion(): void {
    this.jurisdiccionForm.get('jurisdiccion')?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged()
      )
      .subscribe((jurisdiccion: BaseDto | null) => {
        if (jurisdiccion) {
          this.habilitarFuero();
        } else {
          this.deshabilitarFuero();
          this.deshabilitarJuzgado();
        }
        this.filtrarJuzgadosPorSeleccion();
      });
  }

  private configurarCambiosFuero(): void {
    this.fueroForm.get('fuero')?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged()
      )
      .subscribe((fuero: BaseDto | null) => {
        if (fuero) {
          this.habilitarJuzgado();
        } else {
          this.deshabilitarJuzgado();
        }
        this.filtrarJuzgadosPorSeleccion();
      });
  }

  private configurarCambiosJuzgado(): void {
    this.juzgadoForm.get('juzgado')?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged()
      )
      .subscribe((juzgado: JuzgadoDto | null) => {
        this.emitirSeleccion();
      });
  }

  private filtrarJurisdiccionesPorProvincia(): void {
    const provincia = this.provinciaForm.get('provincia')?.value;

    if (!provincia) {
      this.jurisdicciones = [];
      return;
    }

    this.loadingJurisdicciones = true;

    this.jurisdiccionService.getAllByIdProvincia(provincia.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loadingJurisdicciones = false)
      )
      .subscribe({
        next: (jurisdicciones) => {
          this.jurisdicciones = Array.isArray(jurisdicciones) ? jurisdicciones : [];
          // Limpiar selección de jurisdicción si ya no está disponible
          const jurisdiccionActual = this.jurisdiccionForm.get('jurisdiccion')?.value;
          if (jurisdiccionActual && !this.jurisdicciones.find(j => j.id === jurisdiccionActual.id)) {
            this.jurisdiccionForm.get('jurisdiccion')?.setValue(null);
          }
        },
        error: (error) => {
          console.error('Error al filtrar jurisdicciones:', error);
          this.jurisdicciones = [];
        }
      });
  }

  private filtrarJuzgadosPorSeleccion(): void {
    const jurisdiccion = this.jurisdiccionForm.get('jurisdiccion')?.value;
    const fuero = this.fueroForm.get('fuero')?.value;

    if (!jurisdiccion) {
      this.juzgados = [];
      return;
    }

    this.loadingJuzgados = true;

    const filtro: JuzgadoFilter = new JuzgadoFilter();

    filtro.idsJurisdiccion = jurisdiccion ? [jurisdiccion.id] : [];
    filtro.idsFuero = fuero ? [fuero.id] : [];

    this.juzgadoService.getAllByFilter(filtro)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loadingJuzgados = false)
      )
      .subscribe({
        next: (juzgados) => {
          this.juzgados = juzgados;
          // Limpiar selección de juzgado si ya no está disponible
          const juzgadoActual = this.juzgadoForm.get('juzgado')?.value;
          if (juzgadoActual && !juzgados.find(j => j.id === juzgadoActual.id)) {
            this.juzgadoForm.get('juzgado')?.setValue(null);
          }
        },
        error: (error) => {
          console.error('Error al filtrar juzgados:', error);
          this.juzgados = [];
        }
      });
  }

  private habilitarJurisdiccion(): void {
    this.jurisdiccionForm.get('jurisdiccion')?.enable();
    this.jurisdiccionControl.enable();
  }

  private deshabilitarJurisdiccion(): void {
    this.jurisdiccionForm.get('jurisdiccion')?.disable();
    this.jurisdiccionForm.get('jurisdiccion')?.setValue(null);
    this.jurisdiccionControl.disable();
    this.jurisdiccionControl.setValue('');
    this.jurisdicciones = [];
  }

  private habilitarFuero(): void {
    this.fueroForm.get('fuero')?.enable();
    this.fueroControl.enable();
  }

  private deshabilitarFuero(): void {
    this.fueroForm.get('fuero')?.disable();
    this.fueroForm.get('fuero')?.setValue(null);
    this.fueroControl.disable();
    this.fueroControl.setValue('');
  }

  private habilitarJuzgado(): void {
    this.juzgadoForm.get('juzgado')?.enable();
    this.juzgadoControl.enable();
  }

  private deshabilitarJuzgado(): void {
    this.juzgadoForm.get('juzgado')?.disable();
    this.juzgadoForm.get('juzgado')?.setValue(null);
    this.juzgadoControl.disable();
    this.juzgadoControl.setValue('');
    this.juzgados = [];
  }

  private establecerValoresIniciales(): void {
    if (this.provinciaInicial) {
      this.provinciaForm.get('provincia')?.setValue(this.provinciaInicial);
    }
    if (this.jurisdiccionInicial) {
      this.jurisdiccionForm.get('jurisdiccion')?.setValue(this.jurisdiccionInicial);
    }
    if (this.fueroInicial) {
      this.fueroForm.get('fuero')?.setValue(this.fueroInicial);
    }
    if (this.juzgadoInicial) {
      this.juzgadoForm.get('juzgado')?.setValue(this.juzgadoInicial);
    }
  }

  private emitirSeleccion(): void {
    const provincia = this.provinciaForm.get('provincia')?.value ?? null;
    const jurisdiccion = this.jurisdiccionForm.get('jurisdiccion')?.value ?? null;
    const fuero = this.fueroForm.get('fuero')?.value ?? null;
    const juzgado = this.juzgadoForm.get('juzgado')?.value ?? null;

    this.juzgadoSeleccionado.emit({
      provincia,
      jurisdiccion,
      fuero,
      juzgado
    });
  }

  // Métodos de filtrado para autocomplete
  private filtrarProvincias(value: string): ProvinciaDto[] {
    if (!value) return this.provincias;
    const filterValue = value.toLowerCase();
    return this.provincias.filter(option =>
      option.nombre?.toLowerCase().includes(filterValue)
    );
  }

  private filtrarJurisdicciones(value: string): BaseDto[] {
    if (!value) return this.jurisdicciones;
    const filterValue = value.toLowerCase();
    return this.jurisdicciones.filter(option =>
      option.descripcion?.toLowerCase().includes(filterValue)
    );
  }

  private filtrarFueros(value: string): BaseDto[] {
    if (!value) return this.fueros;
    const filterValue = value.toLowerCase();
    return this.fueros.filter(option =>
      option.descripcion?.toLowerCase().includes(filterValue)
    );
  }

  private filtrarJuzgados(value: string): JuzgadoDto[] {
    if (!value) return this.juzgados;
    const filterValue = value.toLowerCase();
    return this.juzgados.filter(option =>
      option.descripcion?.toLowerCase().includes(filterValue)
    );
  }

  // Métodos para mostrar valores en autocomplete
  displayProvincia(provincia: ProvinciaDto): string {
    return provincia ? provincia.nombre : '';
  }

  displayJurisdiccion(jurisdiccion: BaseDto): string {
    return jurisdiccion ? jurisdiccion.descripcion : '';
  }

  displayFuero(fuero: BaseDto): string {
    return fuero ? fuero.descripcion : '';
  }

  displayJuzgado(juzgado: JuzgadoDto): string {
    return juzgado ? juzgado.descripcion : '';
  }

  // Métodos para selección
  onProvinciaSeleccionada(provincia: ProvinciaDto): void {
    this.provinciaForm.get('provincia')?.setValue(provincia);
  }

  onJurisdiccionSeleccionada(jurisdiccion: BaseDto): void {
    this.jurisdiccionForm.get('jurisdiccion')?.setValue(jurisdiccion);
  }

  onFueroSeleccionado(fuero: BaseDto): void {
    this.fueroForm.get('fuero')?.setValue(fuero);
  }

  onJuzgadoSeleccionado(juzgado: JuzgadoDto): void {
    this.juzgadoForm.get('juzgado')?.setValue(juzgado);
  }

  // Métodos públicos para validación
  get provinciaInvalida(): boolean {
    const control = this.provinciaForm.get('provincia');
    return !!(control?.invalid && (control?.dirty || control?.touched));
  }

  get jurisdiccionInvalida(): boolean {
    const control = this.jurisdiccionForm.get('jurisdiccion');
    return !!(control?.invalid && (control?.dirty || control?.touched));
  }

  get fueroInvalido(): boolean {
    const control = this.fueroForm.get('fuero');
    return !!(control?.invalid && (control?.dirty || control?.touched));
  }

  get juzgadoInvalido(): boolean {
    const control = this.juzgadoForm.get('juzgado');
    return !!(control?.invalid && (control?.dirty || control?.touched));
  }

  // Método para limpiar selecciones
  limpiarSelecciones(): void {
    this.provinciaForm.get('provincia')?.setValue(null);
    this.jurisdiccionForm.get('jurisdiccion')?.setValue(null);
    this.fueroForm.get('fuero')?.setValue(null);
    this.juzgadoForm.get('juzgado')?.setValue(null);
    this.provinciaControl.setValue('');
    this.jurisdiccionControl.setValue('');
    this.fueroControl.setValue('');
    this.juzgadoControl.setValue('');
  }

  // Métodos de validación pública
  validarTodoElFormulario(): boolean {
    // Marcar todos los campos como touched para mostrar errores
    this.provinciaForm.markAllAsTouched();
    this.jurisdiccionForm.markAllAsTouched();
    this.fueroForm.markAllAsTouched();
    this.juzgadoForm.markAllAsTouched();

    return this.esFormularioValido();
  }

  esFormularioValido(): boolean {
    return this.provinciaForm.valid &&
           this.jurisdiccionForm.valid &&
           this.fueroForm.valid &&
           this.juzgadoForm.valid;
  }

  obtenerErrores(): string[] {
    const errores: string[] = [];

    if (this.provinciaInvalida) {
      errores.push('Provincia es requerida');
    }
    if (this.jurisdiccionInvalida) {
      errores.push('Jurisdicción es requerida');
    }
    if (this.fueroInvalido) {
      errores.push('Fuero es requerido');
    }
    if (this.juzgadoInvalido) {
      errores.push('Juzgado es requerido');
    }

    return errores;
  }

  // Método para resetear validación
  resetearValidacion(): void {
    this.provinciaForm.markAsUntouched();
    this.jurisdiccionForm.markAsUntouched();
    this.fueroForm.markAsUntouched();
    this.juzgadoForm.markAsUntouched();
  }
}
